import {
    DEFAULT_CLINIC_MASCOT,
    MASCOT_EAR_OPTIONS,
    MASCOT_FUR_OPTIONS,
    MASCOT_PATTERN_OPTIONS,
    MASCOT_SNOUT_OPTIONS,
    resolveMascotEarStyle,
    resolveMascotPattern,
    resolveMascotSnout,
    resolveMascotSpecies,
    sanitizeMascotName,
    sanitizeMascotNickname,
    type ClinicMascotIdentity,
    type MascotEarStyle,
    type MascotFurType,
    type MascotPattern,
    type MascotSnout,
    type MascotSpecies,
} from './mascot-types'

const STORAGE_PREFIX = 'chckup.askChckvet.mascot.v1'

/**
 * Fired on `window` after {@link saveClinicMascot} writes, so every
 * `useClinicMascot` instance in this tab reloads. The native `storage` event
 * only fires in *other* tabs, so same-tab callers (trigger, dock, editor)
 * would otherwise stay stale.
 */
export const CLINIC_MASCOT_UPDATED_EVENT = 'chckup:clinic-mascot-updated'

export type ClinicMascotUpdatedDetail = {
    clinicId: string
}

const SPECIES = new Set<MascotSpecies>([
    'regular_dog',
    'cat',
    'horse',
    'rabbit',
])

const PATTERNS = new Set<MascotPattern>(
    Object.values(MASCOT_PATTERN_OPTIONS).flatMap(options =>
        options.map(o => o.id),
    ),
)

const FUR_TYPES = new Set<MascotFurType>(MASCOT_FUR_OPTIONS.map(o => o.id))

const SNOUTS = new Set<MascotSnout>(
    Object.values(MASCOT_SNOUT_OPTIONS).flatMap(options =>
        options.map(o => o.id),
    ),
)

const EAR_STYLES = new Set<MascotEarStyle>(
    Object.values(MASCOT_EAR_OPTIONS).flatMap(options =>
        options.map(o => o.id),
    ),
)

function storageKey(clinicId: string): string {
    return `${STORAGE_PREFIX}:${clinicId}`
}

function isHexColor(value: unknown): value is string {
    return (
        typeof value === 'string' &&
        /^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/.test(value)
    )
}

/**
 * Normalize unknown JSON into a valid identity. Unknown / partial values
 * fall back field-by-field so a future schema bump doesn't wipe the clinic's
 * mascot.
 */
export function normalizeClinicMascot(raw: unknown): ClinicMascotIdentity {
    if (!raw || typeof raw !== 'object') {
        return { ...DEFAULT_CLINIC_MASCOT }
    }
    const o = raw as Record<string, unknown>
    // Broad-head was a second dog skull; the flat muzzle on the remaining
    // dog covers that look, so carry those mascots across rather than
    // resetting them to a long-muzzle retriever.
    const wasSmoosh = o.species === 'smoosh_dog'
    const species: MascotSpecies = resolveMascotSpecies(
        wasSmoosh
            ? 'regular_dog'
            : typeof o.species === 'string' &&
                SPECIES.has(o.species as MascotSpecies)
              ? (o.species as MascotSpecies)
              : DEFAULT_CLINIC_MASCOT.species,
    )
    // `spots` was dropped — it was four circles scattered on the face, which
    // isn't a marking any animal has. Piebald is the nearest real pattern.
    const savedPattern =
        typeof o.pattern === 'string' && o.pattern === 'spots'
            ? 'patched'
            : typeof o.pattern === 'string' &&
                PATTERNS.has(o.pattern as MascotPattern)
              ? (o.pattern as MascotPattern)
              : DEFAULT_CLINIC_MASCOT.pattern
    // Markings are species-scoped, so a tabby cat switched to a horse has to
    // land on something a horse can actually have.
    const pattern = resolveMascotPattern(species, savedPattern)
    // Ears predate nothing — but a mascot saved before ear styles existed, or
    // one whose species changed, still has to land on something this species
    // actually offers.
    const savedEarStyle =
        typeof o.earStyle === 'string' &&
        EAR_STYLES.has(o.earStyle as MascotEarStyle)
            ? (o.earStyle as MascotEarStyle)
            : MASCOT_EAR_OPTIONS[species][0].id
    // Cats used to borrow the dog's drop ear for their "Folded" option. They
    // have a real folded ear now, so carry those mascots across rather than
    // letting the fallback below reset them to pointed.
    // Dogs used to offer a separate "Folded" (tipped) shape; it was a worse
    // rose, so those mascots become rose.
    const earStyle = resolveMascotEarStyle(
        species,
        species === 'cat' && savedEarStyle === 'floppy'
            ? 'folded'
            : species === 'regular_dog' && o.earStyle === 'tipped'
              ? 'folded'
              : savedEarStyle,
    )

    return {
        species,
        earStyle,
        primaryColor: isHexColor(o.primaryColor)
            ? o.primaryColor
            : DEFAULT_CLINIC_MASCOT.primaryColor,
        secondaryColor: isHexColor(o.secondaryColor)
            ? o.secondaryColor
            : DEFAULT_CLINIC_MASCOT.secondaryColor,
        eyeColor: isHexColor(o.eyeColor)
            ? o.eyeColor
            : DEFAULT_CLINIC_MASCOT.eyeColor,
        furType:
            typeof o.furType === 'string' &&
            FUR_TYPES.has(o.furType as MascotFurType)
                ? (o.furType as MascotFurType)
                : DEFAULT_CLINIC_MASCOT.furType,
        // Snouts are species-scoped, so a mascot that changed species has to
        // land on a muzzle that species offers.
        snout: resolveMascotSnout(
            species,
            typeof o.snout === 'string' && SNOUTS.has(o.snout as MascotSnout)
                ? (o.snout as MascotSnout)
                : wasSmoosh
                  ? 'flat'
                  : MASCOT_SNOUT_OPTIONS[species][0].id,
        ),
        pattern,
        name:
            typeof o.name === 'string'
                ? sanitizeMascotName(o.name)
                : DEFAULT_CLINIC_MASCOT.name,
        nickname:
            typeof o.nickname === 'string'
                ? sanitizeMascotNickname(o.nickname)
                : DEFAULT_CLINIC_MASCOT.nickname,
    }
}

export function loadClinicMascot(clinicId: string): ClinicMascotIdentity {
    if (typeof window === 'undefined' || !clinicId) {
        return { ...DEFAULT_CLINIC_MASCOT }
    }
    try {
        const raw = window.localStorage.getItem(storageKey(clinicId))
        if (!raw) return { ...DEFAULT_CLINIC_MASCOT }
        return normalizeClinicMascot(JSON.parse(raw) as unknown)
    } catch {
        return { ...DEFAULT_CLINIC_MASCOT }
    }
}

export function saveClinicMascot(
    clinicId: string,
    identity: ClinicMascotIdentity,
): void {
    if (typeof window === 'undefined' || !clinicId) return
    try {
        const normalized = normalizeClinicMascot(identity)
        window.localStorage.setItem(
            storageKey(clinicId),
            JSON.stringify(normalized),
        )
        window.dispatchEvent(
            new CustomEvent<ClinicMascotUpdatedDetail>(
                CLINIC_MASCOT_UPDATED_EVENT,
                { detail: { clinicId } },
            ),
        )
    } catch {
        // Quota / private mode — ignore; in-memory UI still works for the session.
    }
}

/** Re-read the stored identity for `clinicId` (shared reload helper). */
export function reloadClinicMascot(clinicId: string): ClinicMascotIdentity {
    return loadClinicMascot(clinicId)
}
